// SuggestionDock.jsx — Area B: the collapsible Suggestion Dock that replaces the old 2x2
// .st-suggest cards in the agent chat empty state. See tmp/build-hub-agent-ux/specs/area-B.md
// (§5 component behavior, §6 measurements) and specs/handoff-digest.md (behavior contract).
//
// Mounted twice across the empty/conversation ternary in AgentStudioChat.jsx. Both mounts request
// an open fresh-load state. Module-scoped page-lifetime state carries manual header choices and
// prevents the second mount from reopening after a suggestion was picked and collapsed the dock.
// Every interactive element here is a real, styled
// <button> (no bare/native gray buttons — CLAUDE.md button rule). DockCard's outer `.sg-card` is
// a plain, non-interactive <div> — only its header row (`.sg-card-hd`) is a real
// <button type="button">, and the CTA is a separate sibling <button> inside `.sg-card-body`.
// Neither button nests inside the other, and clicking the body's `.sg-desc` text no longer
// toggles the card (see tmp/build-hub-agent-chat-visual-fix/specs/dock-fix.md bug #2).
// Picking a suggestion (handlePick below) synchronously collapses the whole dock and retires the
// glow for this page load (Task 2 / 02-chat-suggestions.md).

import React, { useEffect, useState } from "react"

// Module scope (D-T2-2 / D-T2-5): survives the empty→conversation ternary REMOUNT so the
// reopen-hint + glow-retirement stick right after a first-message pick; resets to false on a
// fresh page load so the breathing glow returns on the next visit until the dock is used again.
let dockUsedThisPage = false
// null means the user has not manually changed the dock yet, so defaultOpen may seed it.
// Once the header is clicked (or a suggestion is picked), this bridges the chosen open state
// across AgentStudioChat's empty↔conversation remount for the rest of the page lifetime.
let dockOpenThisPage = null

function resolveDockOpen(defaultOpen) {
  return dockOpenThisPage === null
    ? !!defaultOpen && !dockUsedThisPage
    : dockOpenThisPage
}

function SparkleSVG() {
  return (
    <svg className="ic" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.937A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.964 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M20 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 5h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 18H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChevronSVG({ size = 16 }) {
  return (
    <svg className="cv" width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function BoltSVG() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function RepeatSVG() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M17 2l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 12v-2a4 4 0 0 1 4-4h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 22l-4-4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 12v2a4 4 0 0 1-4 4H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SendSVG() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M22 2 11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 2 15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const TAB_HINT = Object.freeze({
  task: "Example tasks I can do for you right now.",
  job: "Example Agent Jobs that I can set up for you that will automatically monitor your marketing opportunities and take action for you while you're away.",
})

const TAB_LABEL = Object.freeze({ task: "Quick tasks", job: "Agent jobs" })
const CTA_LABEL = Object.freeze({ task: "Run this now", job: "Set up this job" })

// `.sg-card` is a plain, non-interactive <div> — only the header row (`.sg-card-hd`) toggles the
// card, and it's a real <button type="button"> (native keyboard support, no role/tabIndex
// needed). The CTA is a separate sibling <button> living inside `.sg-card-body`, never nested
// inside the header button, so clicking anywhere in the open `.sg-desc` text does NOT collapse
// the card — only clicking the header row (or activating it via keyboard) does. The anonymous
// wrapper <div> inside `.sg-card-body` matches the handoff DOM exactly: it's the element the
// `.sg-card-body > div { overflow: hidden }` grid-collapse rule targets, so `.sg-card-inner`'s
// own padding never leaks into the collapsed (0fr) row (dock-fix.md bug #1).
function DockCard({ item, isOpen, onToggle, onPick }) {
  return (
    <div className={`sg-card${isOpen ? " open" : ""}`}>
      <button
        type="button"
        className="sg-card-hd"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span className="hd-text">
          <span className="row">
            <span className="tt">{item.title}</span>
            <span className="sg-ch"><i style={{ background: item.chColor }} />{item.chLabel}</span>
          </span>
          {!isOpen && (
            <span className="sg-peek-row">
              <span className="peek">{item.desc}</span>
              <span className="sg-more">Read more</span>
            </span>
          )}
        </span>
      </button>
      <div className="sg-card-body">
        <div>
          <div className="sg-card-inner">
            <p className="sg-desc">{item.desc}</p>
            <button
              type="button"
              className={`sg-cta${item.kind === "job" ? " sg-cta--job" : ""}`}
              onClick={() => onPick(item)}
            >
              <SendSVG />
              {CTA_LABEL[item.kind]}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Item #7: `onLibraryNav` (optional) fires on the View-all click so the host can stage the library
// return descriptor (open chat + panel view) before the anchor's own navigation proceeds.
export default function SuggestionDock({ defaultOpen, items, onPick, onExpand, libraryHref, onLibraryNav }) {
  const [open, setOpen] = useState(() => resolveDockOpen(defaultOpen))
  const [tab, setTab] = useState("task")
  const [openCard, setOpenCard] = useState(null)
  const [used, setUsed] = useState(dockUsedThisPage)
  const [hint, setHint] = useState(dockUsedThisPage)

  // Re-sync when the requested default changes, without defeating collapse-on-pick across the
  // empty→conversation remount or a manual header choice. This is not the auto-widen trigger.
  useEffect(() => {
    setOpen(resolveDockOpen(defaultOpen))
  }, [defaultOpen])

  const list = items || []
  const nTask = list.filter((i) => i.kind === "task").length
  const nJob = list.filter((i) => i.kind === "job").length
  const visible = list.filter((i) => i.kind === tab)

  const onTabClick = (kind) => {
    if (kind === tab) return
    setTab(kind)
    setOpenCard(null)
  }

  // Area 5: fire the auto-widen callback ONLY on the deliberate collapsed→open header click —
  // never on the defaultOpen-driven useEffect above, and never on collapse (open→closed must not
  // auto-narrow the panel back). `onExpand` is optional — a safe no-op (`onExpand?.()`) anywhere
  // this component mounts without the shell's resizable panel (e.g. Standard mode).
  const onHeaderClick = () => {
    if (!open) onExpand?.()
    if (!open) setHint(false)
    const nextOpen = !open
    dockOpenThisPage = nextOpen
    setOpen(nextOpen)
  }

  // D-T2-1: picking a suggestion deterministically collapses the WHOLE dock in both mounts (today
  // the conversation mount stays open after a pick). Collapse is synchronous — it does not depend
  // on the send resolving. NEVER calls onExpand (auto-widen stays header-click-only). D-T2-2: the
  // module flag retires the collapsed breathing glow for the rest of this page load, and seeds the
  // one-time "reopen anytime" hint that survives the empty→conversation remount (D-T2-5).
  const handlePick = (item) => {
    dockUsedThisPage = true
    dockOpenThisPage = false
    setUsed(true)
    setHint(true)
    setOpenCard(null)
    setOpen(false)
    onPick(item)
  }

  return (
    <div className={`sg-dock${open ? " open" : ""}${used ? " sg-dock--used" : ""}`}>
      <button
        type="button"
        className="sg-dock-hd"
        aria-expanded={open}
        onClick={onHeaderClick}
      >
        <SparkleSVG />
        <span className="t">Suggestions</span>
        <span className="s">{hint ? "Suggestions live here — reopen anytime" : `${nTask} quick tasks · ${nJob} agent jobs`}</span>
        <span className="sp" />
        <ChevronSVG />
      </button>
      <div className="sg-dock-body">
        <div>
          <div className="sg-dock-in">
            <div className="sg-dock-toggle">
              <div className="sg-dock-toolbar">
                <div className="sg-seg" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "task"}
                    className={tab === "task" ? "on" : ""}
                    onClick={() => onTabClick("task")}
                  >
                    <span className="ki task"><BoltSVG /></span>
                    {TAB_LABEL.task}
                    <span className="ct">{nTask}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "job"}
                    className={tab === "job" ? "on" : ""}
                    onClick={() => onTabClick("job")}
                  >
                    <span className="ki job"><RepeatSVG /></span>
                    {TAB_LABEL.job}
                    <span className="ct">{nJob}</span>
                  </button>
                </div>
              </div>
              <p className="sg-tabs-hint">
                <span>{TAB_HINT[tab]}</span>
                {libraryHref && (
                  <a className="sg-view-all" href={libraryHref} onClick={onLibraryNav}>View all →</a>
                )}
              </p>
            </div>
            <div className="sg-dock-scroll">
              <div className="sg-grid">
                {visible.map((item) => (
                  <DockCard
                    key={item.id}
                    item={item}
                    isOpen={openCard === item.id}
                    onToggle={() => setOpenCard((cur) => (cur === item.id ? null : item.id))}
                    onPick={handlePick}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
